/**
 * System module service (Wave 3.0 skeleton + Wave 3.6 dashboard additions).
 *
 * Covers the 11 System pages: org settings, roles & permissions, worker
 * access, worker groups, notifications config, audit, subscription,
 * billing. The surfaces consumed by Dashboard widgets (RecentAuditLogs,
 * AccessControlOverview) are typed against the generated schema; the
 * rest stay `unknown` until their pages migrate.
 *
 * Naming baseline (HANDOFF §21.5): Entity = Worker throughout code +
 * API + DB + UI. "User" is reserved for svdy-authorization.User (global
 * identity). Tenant-scoped access management therefore uses
 * `SystemWorker*` / `WorkerGroup*` / `/v1/system/workers/*`.
 */

import { http } from './client';
import type { Schema } from './types';

// Typed response shapes used by dashboard widgets.
export type Role = Schema<'RoleResponse'>;
export type Permission = Schema<'PermissionResponse'>;
export type SystemWorker = Schema<'SystemWorkerResponse'>;
export type WorkerGroup = Schema<'WorkerGroupResponse'>;
export type WorkerGroupMember = Schema<'WorkerGroupMemberResponse'>;
export type AuditLog = Schema<'AuditLogResponse'>;
export type SystemOverview = Schema<'SystemOverviewResponse'>;

// ---------------------------------------------------------------------------
// General settings
// ---------------------------------------------------------------------------

export const systemSettingsApi = {
  getSecurity: () => http.get<{ id: string; category: string; data: Record<string, unknown>; updated_at: string | null }>('/v1/system/settings/security'),
  updateSecurity: (data: Record<string, unknown>) => http.patch('/v1/system/settings/security', { data }),
  /**
   * Workers settings — Add Worker dialog defaults (P1.2 Phase B).
   * data shape (all optional):
   *   { default_department_id, default_position_id,
   *     default_location_id, default_employment_type_id }
   * PATCH merges so a partial edit doesn't blow away other defaults.
   */
  getWorkers: () => http.get<{ id: string; category: string; data: WorkersSettingsData; updated_at: string | null }>('/v1/system/settings/workers'),
  updateWorkers: (data: WorkersSettingsData) => http.patch('/v1/system/settings/workers', { data }),
};

/**
 * Enterprise SSO IdP configuration (ADR-022 Phase 1).
 *
 * Admin self-service at /system/settings/sso. The
 * ``oidc_client_secret`` field is write-only — the response never
 * includes it; ``oidc_client_secret_set`` reports whether one is
 * on file so the UI can render correct CTAs without ever holding
 * the value.
 *
 * Save & Test (D3) calls /test which fetches the discovery URL
 * and validates the OIDC metadata. Returns ok=false with a
 * descriptive detail on any failure (URL unreachable, malformed
 * JSON, missing endpoint keys).
 */
export const systemSsoApi = {
  get: () =>
    http.get<SsoConfigResponse | null>('/v1/system/sso/config'),
  upsert: (data: SsoConfigUpsert) =>
    http.put<SsoConfigResponse>('/v1/system/sso/config', data),
  remove: () =>
    http.delete<{ deleted: boolean }>('/v1/system/sso/config'),
  test: () =>
    http.post<SsoConfigTestResponse>('/v1/system/sso/config/test'),
};

/**
 * Per-tenant strict-SSO toggle (ADR-025 Phase 4). Read pre-flight
 * before flipping; flip is gated server-side on pre-flight.
 * Emergency-disable is the IdP-outage escape hatch; requires a
 * reason ≥10 chars for audit.
 */
export const systemSsoStrictModeApi = {
  status: () =>
    http.get<SsoStrictModeStatus>('/v1/system/sso/strict-mode/status'),
  preFlight: () =>
    http.get<SsoStrictModePreflightResponse>(
      '/v1/system/sso/strict-mode/pre-flight',
    ),
  flip: (enable: boolean) =>
    http.post<SsoStrictModeStatus>('/v1/system/sso/strict-mode/flip', {
      enable,
    }),
  emergencyDisable: (reason: string) =>
    http.post<SsoStrictModeStatus>(
      '/v1/system/sso/strict-mode/emergency-disable',
      { reason },
    ),
};

/**
 * SCIM 2.0 provisioning (ADR-024 Phase 3.A admin token CRUD +
 * Phase 3.B group-mapping CRUD). Token plaintext is returned ONCE
 * on generate/rotate; status response NEVER includes plaintext —
 * UI must show the show-once modal at generate-time.
 */
export const systemScimApi = {
  getTokenStatus: () =>
    http.get<ScimTokenStatusResponse>('/v1/system/sso/scim-token'),
  generateToken: () =>
    http.post<ScimTokenGenerateResponse>('/v1/system/sso/scim-token'),
  revokeToken: () =>
    http.delete<{ revoked: boolean }>('/v1/system/sso/scim-token'),
  listGroupMappings: () =>
    http.get<ScimGroupMappingResponse[]>(
      '/v1/system/sso/scim/group-mappings',
    ),
  createGroupMapping: (data: ScimGroupMappingUpsert) =>
    http.post<ScimGroupMappingResponse>(
      '/v1/system/sso/scim/group-mappings', data,
    ),
  deleteGroupMapping: (mappingId: string) =>
    http.delete<void>(
      `/v1/system/sso/scim/group-mappings/${mappingId}`,
    ),
};

export interface ScimTokenStatusResponse {
  configured: boolean;
  token_prefix?: string | null;
  created_at?: string | null;
  last_used_at?: string | null;
  last_rotated_at?: string | null;
  request_count: number;
}

export interface ScimTokenGenerateResponse {
  id: string;
  /** Plaintext bearer token — shown ONCE on generation. The
   *  GET status endpoint never returns this field. */
  token: string;
  token_prefix: string;
  created_at: string;
}

export interface ScimGroupMappingResponse {
  id: string;
  idp_group_id: string;
  idp_group_display_name: string | null;
  target_kind: 'department' | 'role';
  target_id: string;
  target_display_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScimGroupMappingUpsert {
  idp_group_id: string;
  idp_group_display_name?: string | null;
  target_kind: 'department' | 'role';
  target_id: string;
}

export interface SsoStrictModeStatus {
  organization_id: string;
  strict_mode: boolean;
  activated_at: string | null;
  activated_by_user_id: string | null;
}

export interface SsoStrictModePreflightCheck {
  id: 'active_sso_config' | 'break_glass_admin' | 'all_workers_migrated';
  passed: boolean;
  detail: string;
  /** Only populated for 'all_workers_migrated' check. */
  unmigrated_count?: number | null;
}

export interface SsoStrictModePreflightResponse {
  ready: boolean;
  checks: SsoStrictModePreflightCheck[];
}

export interface SsoConfigUpsert {
  /** Phase 2.A.1+: SAML supported alongside OIDC. */
  protocol: 'oidc' | 'saml';
  email_domain: string;
  is_active: boolean;
  display_name?: string | null;
  enable_jit_provisioning: boolean;
  jit_email_domain_whitelist?: string[] | null;
  // OIDC fields (required when protocol='oidc' on create; partial
  // on update — service preserves existing for omitted fields).
  oidc_discovery_url?: string | null;
  oidc_client_id?: string | null;
  /** Plaintext on the wire; encrypted server-side. Empty / omitted
   *  on update preserves the existing encrypted value. */
  oidc_client_secret?: string | null;
  oidc_scopes: string;
  oidc_email_claim: string;
  // SAML fields (required when protocol='saml' on create; partial
  // on update). The certificate is the IdP's PUBLIC key (plaintext
  // OK; nothing secret about it). saml_email_attribute defaults
  // to 'NameID' which covers the spec fall-back.
  saml_entity_id?: string | null;
  saml_sso_url?: string | null;
  saml_certificate?: string | null;
  saml_email_attribute: string;
}

export interface SsoConfigResponse {
  id: string;
  organization_id: string;
  protocol: 'oidc' | 'saml';
  email_domain: string;
  is_active: boolean;
  display_name: string | null;
  enable_jit_provisioning: boolean;
  jit_email_domain_whitelist: string[] | null;
  oidc_discovery_url: string | null;
  oidc_client_id: string | null;
  /** Server-derived: true iff a secret is on file. UI uses this to
   *  show "Replace" vs "Set" CTA. The secret itself never crosses
   *  this boundary. */
  oidc_client_secret_set: boolean;
  oidc_scopes: string;
  oidc_email_claim: string;
  saml_entity_id: string | null;
  saml_sso_url: string | null;
  saml_certificate: string | null;
  saml_email_attribute: string;
  /** Server-derived from cert PEM via cryptography.x509 parse.
   *  ISO-8601 datetime; UI uses this for the < 30 days expiry
   *  warning banner. NULL when no cert / parse failure. */
  saml_cert_not_after: string | null;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

export interface SsoConfigTestResponse {
  ok: boolean;
  detail: string;
  discovered_authorization_endpoint: string | null;
  discovered_token_endpoint: string | null;
  discovered_userinfo_endpoint: string | null;
}

/** Type of the JSONB ``data`` blob under the 'workers' settings
 *  category. Every key is optional — an empty object is the legitimate
 *  initial state. */
export interface WorkersSettingsData {
  default_department_id?: string | null;
  default_position_id?: string | null;
  default_location_id?: string | null;
  default_employment_type_id?: string | null;
}

// ---------------------------------------------------------------------------
// Roles + permissions + worker access + worker groups
// ---------------------------------------------------------------------------

export const systemRolesApi = {
  list: () => http.get<Role[]>('/v1/system/roles'),
  create: (data: unknown) => http.post('/v1/system/roles', data),
  update: (id: string, data: unknown) =>
    http.patch(`/v1/system/roles/${id}`, data),
  delete: (id: string) => http.delete(`/v1/system/roles/${id}`),
};

export const systemPermissionsApi = {
  list: () => http.get<Permission[]>('/v1/system/permissions'),
};

/**
 * The CALLER's own permissions, for navigation gating (`useMyPermissions`).
 *
 * Distinct from `systemPermissionsApi.list()` above, which is the CATALOG of every
 * permission and itself requires `system.roles.read`. This one carries no permission gate
 * at all — gating it would be circular, since the user who most needs the answer is the one
 * who holds nothing.
 *
 * `is_superuser` mirrors `require_permission`'s `"*"` short-circuit rather than the list
 * being expanded, so the client's check cannot drift from the server's.
 */
export const myPermissionsApi = {
  get: () => http.get<{ permissions: string[]; is_superuser: boolean }>(
    '/v1/system/me/permissions',
  ),
};

/**
 * Tenant-member access management. Lists workers with their role_code,
 * supports invite/update/terminate/revoke-access flows. The response
 * shape is Worker-centric (worker_code + optional role_code). Login
 * identity lives in svdy-authorization.User; this API is the bridge
 * between the two (via Worker.auth_user_id).
 */
export const systemWorkersApi = {
  list: () => http.get<SystemWorker[]>('/v1/system/workers'),
  get: (id: string) => http.get<SystemWorker>(`/v1/system/workers/${id}`),
  invite: (data: { email: string; first_name: string; last_name: string; role_id: string }) =>
    http.post<{ worker_id: string; email: string; invite_url: string; expires_at: string }>('/v1/system/workers/invite', data),
  update: (id: string, data: { role_id?: string | null; status?: string | null }) =>
    http.patch<SystemWorker>(`/v1/system/workers/${id}`, data),
  /** Soft-delete: status → 'former'. */
  delete: (id: string) => http.delete(`/v1/system/workers/${id}`),
  /**
   * Revoke a worker's login access — clears their role and unsets
   * `auth_user_id` on the Worker row. The worker stays `active`;
   * re-granting access requires a fresh invite.
   */
  revokeAccess: (id: string) =>
    http.delete<SystemWorker>(`/v1/system/workers/${id}/access`),
};

export const workerGroupsApi = {
  list: () => http.get<WorkerGroup[]>('/v1/system/worker-groups'),
  create: (data: unknown) => http.post('/v1/system/worker-groups', data),
  update: (id: string, data: unknown) =>
    http.patch(`/v1/system/worker-groups/${id}`, data),
  delete: (id: string) => http.delete(`/v1/system/worker-groups/${id}`),

  // Member management (P2.4)
  listMembers: (groupId: string) =>
    http.get<WorkerGroupMember[]>(`/v1/system/worker-groups/${groupId}/members`),
  addMembers: (groupId: string, workerIds: string[]) =>
    http.post(`/v1/system/worker-groups/${groupId}/members`, {
      worker_ids: workerIds,
    }),
  removeMember: (groupId: string, workerId: string) =>
    http.delete(`/v1/system/worker-groups/${groupId}/members/${workerId}`),
};

// ---------------------------------------------------------------------------
// Notifications (Wave 3.5 stubs; real page work when migration happens)
// ---------------------------------------------------------------------------

export const notificationsApi = {
  list: () => http.get<unknown[]>('/v1/system/settings/notification-rules'),
  create: (data: { name: string; event_type: string; enabled?: boolean }) =>
    http.post('/v1/system/settings/notification-rules', data),
  update: (id: string, data: { name?: string; event_type?: string; enabled?: boolean }) =>
    http.patch(`/v1/system/settings/notification-rules/${id}`, data),
  delete: (id: string) => http.delete(`/v1/system/settings/notification-rules/${id}`),
};

// IM webhook settings — REMOVED 2026-05-24 (P0.1 Path A3 cutover).
// OAuth2 install + management lives at /v1/integrations/{provider}/oauth/*.
// See svdy-platform/IM_INTEGRATION_ARCHITECTURE.md.

// ---------------------------------------------------------------------------
// Audit logs — backend splits activity (who did what) vs access (who saw
// what). Dashboard's RecentAuditLogs widget surfaces the activity stream.
// ---------------------------------------------------------------------------

export interface AuditLogQuery {
  limit?: number;
  module?: string;
  action?: string;
}

function auditQS(q?: AuditLogQuery): string {
  if (!q) return '';
  const parts: string[] = [];
  if (q.limit != null) parts.push(`limit=${q.limit}`);
  if (q.module) parts.push(`module=${encodeURIComponent(q.module)}`);
  if (q.action) parts.push(`action=${encodeURIComponent(q.action)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const auditApi = {
  /** Activity: mutation events (create/update/delete) across modules. */
  listActivity: (query?: AuditLogQuery) =>
    http.get<AuditLog[]>(`/v1/system/audit-logs/activity${auditQS(query)}`),
  /** Access: read-event stream (API access, data exports). */
  listAccess: (query?: AuditLogQuery) =>
    http.get<AuditLog[]>(`/v1/system/audit-logs/access${auditQS(query)}`),
};

// ---------------------------------------------------------------------------
// System-level overview (counts for the AccessControlOverview widget).
// ---------------------------------------------------------------------------

export const systemOverviewApi = {
  get: () => http.get<SystemOverview>('/v1/system/overview'),
};


// ---------------------------------------------------------------------------
// Worker devices observability (admin) — ADR-021 follow-up.
// ---------------------------------------------------------------------------
//
// Surfaces every WorkerDevice row in the org so the admin Devices
// page can answer "how many of my workers run mobile, on which
// platforms, when did they last open it, and how many tokens have
// been auto-revoked?" Counts + decorated rows arrive in one
// round-trip — backend joins to Worker.first_name + last_name +
// email so the table doesn't need a second fetch to map worker_id.

export interface SystemWorkerDevice {
  id: string;
  worker_id: string;
  worker_name: string | null;
  worker_email: string | null;
  platform: string;            // 'ios' | 'android' | 'web'
  device_id: string | null;
  app_version: string | null;
  registered_at: string;       // ISO datetime
  last_active_at: string;      // ISO datetime
  revoked_at: string | null;
  /** `'active'` / `'pending'` when THIS device holds the worker's single clock-in slot.
   *
   *  Added 2026-09-08. This list is the PUSH registration; punching is authorised by a separate
   *  table, so a device could read `Active` here while the app refused the punch — reported by an
   *  admin who revoked every stale row and still could not let a worker clock in. */
  punch_slot?: string | null;   // null when active
}

export interface SystemDevicesSummary {
  total: number;
  active: number;
  revoked: number;
  ios: number;
  android: number;
  web: number;
  devices: SystemWorkerDevice[];
}

export const systemDevicesApi = {
  /** GET /v1/system/devices. Default returns active + revoked rows.
   *  Pass include_revoked=false to filter to live tokens only. */
  list: (includeRevoked = true) =>
    http.get<SystemDevicesSummary>(
      `/v1/system/devices?include_revoked=${includeRevoked ? 'true' : 'false'}`,
    ),
  /** DELETE /v1/system/devices/{id}. Admin force-revoke (lost / stolen
   *  device path). Idempotent on already-revoked rows. Returns the
   *  updated decorated row. Requires system.workers.manage. */
  revoke: (deviceId: string) =>
    http.delete<SystemWorkerDevice>(`/v1/system/devices/${deviceId}`),
};
